import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    FiArrowLeft,
    FiBriefcase,
    FiCheckCircle,
    FiFilter,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiSlash,
    FiUsers,
    FiX,
} from "react-icons/fi";

import useSwal
    from "../../../../../hooks/useSwal";

import {
    fetchPlatformBusinesses,
    createPlatformBusiness,
    activatePlatformBusiness,
    suspendPlatformBusiness,

    setPlatformBusinessFilter,

    selectPlatformBusinesses,
    selectPlatformBusinessPagination,
    selectPlatformBusinessFilters,
    selectPlatformBusinessListLoading,
    selectPlatformBusinessCreateLoading,
    selectPlatformBusinessStatusLoading,
    selectPlatformBusinessStatusBusinessId,
} from "../../../../../store/platformBusiness/platformBusinessSlice";


import {
    BusinessStatusBadge,
    BusinessFeatureTags,
    BusinessSummaryCard,
    BusinessModal,
    BusinessTableActions,
} from "./components";

import PlatformBusinessForm
    from "./PlatformBusinessForm";

import "./PlatformBusiness.css";


/*
|--------------------------------------------------------------------------
| FORMAT NUMBER
|--------------------------------------------------------------------------
*/

const formatNumber = (
    value
) => {

    return Number(
        value ||
        0
    ).toLocaleString(
        "en-IN"
    );

};


/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {

    if (
        !value
    ) {

        return "-";

    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "-";

    }


    return date.toLocaleDateString(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    );

};


/*
|--------------------------------------------------------------------------
| GET BUSINESS ID
|--------------------------------------------------------------------------
*/

const getBusinessId = (
    business
) => {

    return (
        business?._id ||
        business?.id ||
        null
    );

};


/*
|--------------------------------------------------------------------------
| PLATFORM BUSINESS LIST
|--------------------------------------------------------------------------
*/

const PlatformBusinessList =
    () => {

        const dispatch =
            useDispatch();


        const history =
            useHistory();


        const swal =
            useSwal();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const businesses =
            useSelector(
                selectPlatformBusinesses
            );


        const pagination =
            useSelector(
                selectPlatformBusinessPagination
            );


        const filters =
            useSelector(
                selectPlatformBusinessFilters
            );


        const listLoading =
            useSelector(
                selectPlatformBusinessListLoading
            );


        const createLoading =
            useSelector(
                selectPlatformBusinessCreateLoading
            );


        const statusLoading =
            useSelector(
                selectPlatformBusinessStatusLoading
            );


        const statusBusinessId =
            useSelector(
                selectPlatformBusinessStatusBusinessId
            );


        /*
        |--------------------------------------------------------------------------
        | LOCAL SEARCH
        |--------------------------------------------------------------------------
        */

        const [
            searchInput,
            setSearchInput,
        ] =
            useState(
                filters?.search ||
                ""
            );


        /*
        |--------------------------------------------------------------------------
        | CREATE MODAL
        |--------------------------------------------------------------------------
        */

        const [
            showCreateModal,
            setShowCreateModal,
        ] =
            useState(
                false
            );


        /*
        |--------------------------------------------------------------------------
        | SYNC SEARCH FIELD
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                setSearchInput(
                    filters?.search ||
                    ""
                );

            },
            [
                filters?.search,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | LOAD BUSINESSES
        |--------------------------------------------------------------------------
        |
        | Any Redux filter change automatically refreshes the table.
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchPlatformBusinesses({
                        page:
                            filters?.page ||
                            1,

                        limit:
                            filters?.limit ||
                            20,

                        search:
                            filters?.search ||
                            "",

                        status:
                            filters?.status ||
                            "",
                    })
                );

            },
            [
                dispatch,
                filters?.page,
                filters?.limit,
                filters?.search,
                filters?.status,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | PAGE SUMMARY
        |--------------------------------------------------------------------------
        |
        | This summary represents only businesses currently loaded on the page.
        |--------------------------------------------------------------------------
        */

        const pageSummary =
            useMemo(
                () => {

                    return businesses.reduce(
                        (
                            result,
                            business
                        ) => {

                            const status =
                                String(
                                    business?.status ||
                                    ""
                                )
                                    .trim()
                                    .toUpperCase();


                            if (
                                status ===
                                "ACTIVE"
                            ) {

                                result.active +=
                                    1;

                            }


                            if (
                                status ===
                                "SUSPENDED"
                            ) {

                                result.suspended +=
                                    1;

                            }


                            if (
                                status ===
                                "INACTIVE"
                            ) {

                                result.inactive +=
                                    1;

                            }


                            return result;

                        },
                        {
                            active:
                                0,

                            suspended:
                                0,

                            inactive:
                                0,
                        }
                    );

                },
                [
                    businesses,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | CURRENT PAGE
        |--------------------------------------------------------------------------
        */

        const currentPage =
            Math.max(
                Number(
                    pagination?.page ||
                    filters?.page ||
                    1
                ),
                1
            );


        /*
        |--------------------------------------------------------------------------
        | TOTAL PAGES
        |--------------------------------------------------------------------------
        */

        const totalPages =
            Math.max(
                Number(
                    pagination?.totalPages ||
                    1
                ),
                1
            );


        /*
        |--------------------------------------------------------------------------
        | REFRESH LIST
        |--------------------------------------------------------------------------
        */

        const refreshList =
            (
                overrides = {}
            ) => {

                dispatch(
                    fetchPlatformBusinesses({
                        page:
                            overrides?.page ??
                            filters?.page ??
                            1,

                        limit:
                            overrides?.limit ??
                            filters?.limit ??
                            20,

                        search:
                            overrides?.search ??
                            filters?.search ??
                            "",

                        status:
                            overrides?.status ??
                            filters?.status ??
                            "",
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SEARCH
        |--------------------------------------------------------------------------
        */

        const handleSearch =
            (
                event
            ) => {

                event.preventDefault();


                const value =
                    searchInput.trim();


                /*
                |--------------------------------------------------------------------------
                | RESET PAGE
                |--------------------------------------------------------------------------
                */

                dispatch(
                    setPlatformBusinessFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                /*
                |--------------------------------------------------------------------------
                | SEARCH
                |--------------------------------------------------------------------------
                */

                dispatch(
                    setPlatformBusinessFilter({
                        key:
                            "search",

                        value,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CLEAR SEARCH
        |--------------------------------------------------------------------------
        */

        const handleClearSearch =
            () => {

                setSearchInput(
                    ""
                );


                dispatch(
                    setPlatformBusinessFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformBusinessFilter({
                        key:
                            "search",

                        value:
                            "",
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | STATUS FILTER
        |--------------------------------------------------------------------------
        */

        const handleStatusChange =
            (
                event
            ) => {

                const value =
                    event.target.value;


                dispatch(
                    setPlatformBusinessFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformBusinessFilter({
                        key:
                            "status",

                        value,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CREATE MODAL
        |--------------------------------------------------------------------------
        */

        const handleOpenCreateModal =
            () => {

                setShowCreateModal(
                    true
                );

            };


        const handleCloseCreateModal =
            () => {

                if (
                    createLoading
                ) {

                    return;

                }


                setShowCreateModal(
                    false
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CREATE BUSINESS
        |--------------------------------------------------------------------------
        |
        | PlatformBusinessForm already prepares:
        |
        | {
        |     business: {...},
        |     owner: {...}
        | }
        |--------------------------------------------------------------------------
        */

        const handleCreateBusiness =
            async (
                payload
            ) => {

                const result =
                    await dispatch(
                        createPlatformBusiness(
                            payload
                        )
                    );


                if (
                    createPlatformBusiness
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.success({
                        title:
                            "Business Created",

                        text:
                            "Business and owner account created successfully.",
                    });


                    /*
                    |--------------------------------------------------------------------------
                    | CLOSE MODAL
                    |--------------------------------------------------------------------------
                    */

                    setShowCreateModal(
                        false
                    );


                    /*
                    |--------------------------------------------------------------------------
                    | RETURN TO FIRST PAGE
                    |--------------------------------------------------------------------------
                    */

                    dispatch(
                        setPlatformBusinessFilter({
                            key:
                                "page",

                            value:
                                1,
                        })
                    );


                    /*
                    |--------------------------------------------------------------------------
                    | REFRESH
                    |--------------------------------------------------------------------------
                    */

                    refreshList({
                        page:
                            1,
                    });


                    return;

                }


                swal.error({
                    title:
                        "Unable to Create Business",

                    text:
                        result?.payload
                            ?.message ||
                        "Business creation failed.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | VIEW BUSINESS
        |--------------------------------------------------------------------------
        */

        const handleViewBusiness =
            (
                business
            ) => {

                const id =
                    getBusinessId(
                        business
                    );


                if (
                    !id
                ) {

                    return;

                }


                history.push(
                    `/platform/businesses/${id}`
                );

            };


        /*
        |--------------------------------------------------------------------------
        | ACTIVATE BUSINESS
        |--------------------------------------------------------------------------
        */

        const handleActivate =
            async (
                business
            ) => {

                const id =
                    getBusinessId(
                        business
                    );


                if (
                    !id ||
                    statusLoading
                ) {

                    return;

                }


                const confirmation =
                    await swal.confirm({
                        title:
                            "Activate Business?",

                        text:
                            `${
                                business?.name ||
                                "This business"
                            } will regain access to tenant services.`,

                        confirmButtonText:
                            "Activate",
                    });


                if (
                    confirmation?.isConfirmed ===
                    false
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        activatePlatformBusiness(
                            id
                        )
                    );


                if (
                    activatePlatformBusiness
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.success({
                        title:
                            "Business Activated",

                        text:
                            "Business activated successfully.",
                    });


                    /*
                    |--------------------------------------------------------------------------
                    | REFRESH CURRENT LIST
                    |--------------------------------------------------------------------------
                    */

                    refreshList();


                    return;

                }


                swal.error({
                    title:
                        "Activation Failed",

                    text:
                        result?.payload
                            ?.message ||
                        "Unable to activate business.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | SUSPEND BUSINESS
        |--------------------------------------------------------------------------
        */

        const handleSuspend =
            async (
                business
            ) => {

                const id =
                    getBusinessId(
                        business
                    );


                if (
                    !id ||
                    statusLoading
                ) {

                    return;

                }


                const confirmation =
                    await swal.confirm({
                        title:
                            "Suspend Business?",

                        text:
                            `${
                                business?.name ||
                                "This business"
                            } will lose access to tenant services until reactivated.`,

                        confirmButtonText:
                            "Suspend",
                    });


                if (
                    confirmation?.isConfirmed ===
                    false
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        suspendPlatformBusiness(
                            id
                        )
                    );


                if (
                    suspendPlatformBusiness
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.success({
                        title:
                            "Business Suspended",

                        text:
                            "Business suspended successfully.",
                    });


                    /*
                    |--------------------------------------------------------------------------
                    | REFRESH CURRENT LIST
                    |--------------------------------------------------------------------------
                    */

                    refreshList();


                    return;

                }


                swal.error({
                    title:
                        "Suspension Failed",

                    text:
                        result?.payload
                            ?.message ||
                        "Unable to suspend business.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | PAGINATION
        |--------------------------------------------------------------------------
        */

        const handlePageChange =
            (
                page
            ) => {

                const nextPage =
                    Math.min(
                        Math.max(
                            Number(
                                page
                            ) ||
                            1,
                            1
                        ),
                        totalPages
                    );


                if (
                    nextPage ===
                    currentPage
                ) {

                    return;

                }


                dispatch(
                    setPlatformBusinessFilter({
                        key:
                            "page",

                        value:
                            nextPage,
                    })
                );


                /*
                |--------------------------------------------------------------------------
                | GO TO TOP
                |--------------------------------------------------------------------------
                */

                window.scrollTo({
                    top:
                        0,

                    behavior:
                        "smooth",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

        return (
                <div className="platform-business-page">

                    {/*
                    |--------------------------------------------------------------------------
                    | HEADER
                    |--------------------------------------------------------------------------
                    */}

                    <header className="platform-business-page-header">

                        <div>

                            <button
                                type="button"
                                className="platform-business-back-button"
                                onClick={() =>
                                    history.push(
                                        "/platform/dashboard"
                                    )
                                }
                            >

                                <FiArrowLeft />

                                Dashboard

                            </button>


                            <h1>

                                Business Management

                            </h1>


                            <p>

                                Manage tenant businesses,
                                subscriptions and platform access.

                            </p>

                        </div>


                        <div className="platform-business-header-actions">

                            <button
                                type="button"
                                className="platform-business-refresh-button"
                                onClick={() =>
                                    refreshList()
                                }
                                disabled={
                                    listLoading
                                }
                            >

                                <FiRefreshCw
                                    className={
                                        listLoading
                                            ? "platform-business-spin"
                                            : ""
                                    }
                                />

                                Refresh

                            </button>


                            <button
                                type="button"
                                className="platform-business-create-button"
                                onClick={
                                    handleOpenCreateModal
                                }
                                disabled={
                                    createLoading
                                }
                            >

                                <FiPlus />

                                Create Business

                            </button>

                        </div>

                    </header>


                    {/*
                    |--------------------------------------------------------------------------
                    | SUMMARY
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-business-summary">

                        <BusinessSummaryCard
                            icon={
                                FiBriefcase
                            }
                            label="Total Businesses"
                            value={
                                formatNumber(
                                    pagination?.total
                                )
                            }
                        />


                        <BusinessSummaryCard
                            icon={
                                FiCheckCircle
                            }
                            label="Active On Page"
                            value={
                                pageSummary.active
                            }
                            variant="active"
                        />


                        <BusinessSummaryCard
                            icon={
                                FiSlash
                            }
                            label="Suspended On Page"
                            value={
                                pageSummary.suspended
                            }
                            variant="suspended"
                        />


                        <BusinessSummaryCard
                            icon={
                                FiUsers
                            }
                            label="Current Page"
                            value={
                                `${currentPage} / ${totalPages}`
                            }
                        />

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | TOOLBAR
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-business-toolbar">

                        <form
                            className="platform-business-search"
                            onSubmit={
                                handleSearch
                            }
                        >

                            <FiSearch />


                            <input
                                type="text"
                                value={
                                    searchInput
                                }
                                onChange={(
                                    event
                                ) =>
                                    setSearchInput(
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Search business name, code, mobile or email..."
                            />


                            {searchInput ? (

                                <button
                                    type="button"
                                    className="platform-business-clear-search"
                                    onClick={
                                        handleClearSearch
                                    }
                                    title="Clear search"
                                >

                                    <FiX />

                                </button>

                            ) : null}


                            <button
                                type="submit"
                                className="platform-business-search-button"
                                disabled={
                                    listLoading
                                }
                            >

                                Search

                            </button>

                        </form>


                        <div className="platform-business-filter">

                            <FiFilter />


                            <select
                                value={
                                    filters?.status ||
                                    ""
                                }
                                onChange={
                                    handleStatusChange
                                }
                                disabled={
                                    listLoading
                                }
                            >

                                <option value="">

                                    All Status

                                </option>


                                <option value="ACTIVE">

                                    Active

                                </option>


                                <option value="INACTIVE">

                                    Inactive

                                </option>


                                <option value="SUSPENDED">

                                    Suspended

                                </option>

                            </select>

                        </div>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | TABLE
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-business-table-card">

                        <div className="platform-business-table-wrapper">

                            <table className="platform-business-list-table">

                                <thead>

                                    <tr>

                                        <th>
                                            Business
                                        </th>

                                        <th>
                                            Code
                                        </th>

                                        <th>
                                            Contact
                                        </th>

                                        <th>
                                            Subscription
                                        </th>

                                        <th>
                                            Features
                                        </th>

                                        <th>
                                            Status
                                        </th>

                                        <th>
                                            Created
                                        </th>

                                        <th>
                                            Actions
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {/*
                                    |--------------------------------------------------------------------------
                                    | INITIAL LOADING
                                    |--------------------------------------------------------------------------
                                    */}

                                    {listLoading &&
                                    businesses.length ===
                                        0 ? (

                                        <tr>

                                            <td
                                                colSpan={
                                                    8
                                                }
                                            >

                                                <div className="platform-business-loading">

                                                    <div className="platform-business-loader" />


                                                    <span>

                                                        Loading businesses...

                                                    </span>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : businesses.length >
                                      0 ? (

                                        businesses.map(
                                            (
                                                business
                                            ) => {

                                                const businessId =
                                                    getBusinessId(
                                                        business
                                                    );


                                                const rowLoading =
                                                    statusLoading &&
                                                    String(
                                                        statusBusinessId
                                                    ) ===
                                                        String(
                                                            businessId
                                                        );


                                                return (

                                                    <tr
                                                        key={
                                                            businessId
                                                        }
                                                    >

                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | BUSINESS
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <div className="platform-business-name-cell">

                                                                <div className="platform-business-list-avatar">

                                                                    {String(
                                                                        business?.name ||
                                                                        "B"
                                                                    )
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase()}

                                                                </div>


                                                                <div>

                                                                    <strong>

                                                                        {
                                                                            business?.name ||
                                                                            "-"
                                                                        }

                                                                    </strong>


                                                                    <span>

                                                                        {
                                                                            business?.legalName ||
                                                                            business?.subdomain ||
                                                                            "-"
                                                                        }

                                                                    </span>

                                                                </div>

                                                            </div>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | CODE
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <span className="platform-business-code-badge">

                                                                {
                                                                    business?.code ||
                                                                    "-"
                                                                }

                                                            </span>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | CONTACT
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <div className="platform-business-contact">

                                                                <strong>

                                                                    {
                                                                        business?.mobile ||
                                                                        "-"
                                                                    }

                                                                </strong>


                                                                <span>

                                                                    {
                                                                        business?.email ||
                                                                        "-"
                                                                    }

                                                                </span>

                                                            </div>

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | SUBSCRIPTION
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <BusinessStatusBadge
                                                                status={
                                                                    business
                                                                        ?.subscriptionStatus
                                                                }
                                                            />

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | FEATURES
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <BusinessFeatureTags
                                                                storefrontEnabled={
                                                                    business
                                                                        ?.storefrontEnabled
                                                                }

                                                                marketplaceEnabled={
                                                                    business
                                                                        ?.marketplaceEnabled
                                                                }
                                                            />

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | BUSINESS STATUS
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <BusinessStatusBadge
                                                                status={
                                                                    business
                                                                        ?.status
                                                                }
                                                            />

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | CREATED
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            {
                                                                formatDate(
                                                                    business
                                                                        ?.createdAt
                                                                )
                                                            }

                                                        </td>


                                                        {/*
                                                        |--------------------------------------------------------------------------
                                                        | ACTIONS
                                                        |--------------------------------------------------------------------------
                                                        */}

                                                        <td>

                                                            <BusinessTableActions
                                                                business={
                                                                    business
                                                                }

                                                                loading={
                                                                    rowLoading
                                                                }

                                                                onView={
                                                                    handleViewBusiness
                                                                }

                                                                onActivate={
                                                                    handleActivate
                                                                }

                                                                onSuspend={
                                                                    handleSuspend
                                                                }
                                                            />

                                                        </td>

                                                    </tr>

                                                );

                                            }
                                        )

                                    ) : (

                                        /*
                                        |--------------------------------------------------------------------------
                                        | EMPTY
                                        |--------------------------------------------------------------------------
                                        */

                                        <tr>

                                            <td
                                                colSpan={
                                                    8
                                                }
                                            >

                                                <div className="platform-business-empty">

                                                    <FiBriefcase />


                                                    <strong>

                                                        No businesses found

                                                    </strong>


                                                    <span>

                                                        {filters?.search ||
                                                        filters?.status
                                                            ? "Try changing your search or filters."
                                                            : "Create your first business to get started."}

                                                    </span>


                                                    {!filters?.search &&
                                                    !filters?.status ? (

                                                        <button
                                                            type="button"
                                                            className="platform-business-create-button"
                                                            onClick={
                                                                handleOpenCreateModal
                                                            }
                                                        >

                                                            <FiPlus />

                                                            Create Business

                                                        </button>

                                                    ) : null}

                                                </div>

                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                            </table>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | PAGINATION
                        |--------------------------------------------------------------------------
                        */}

                        <div className="platform-business-pagination">

                            <div>

                                Page{" "}

                                <strong>

                                    {
                                        currentPage
                                    }

                                </strong>

                                {" "}of{" "}

                                <strong>

                                    {
                                        totalPages
                                    }

                                </strong>

                                {" "}·{" "}

                                {
                                    formatNumber(
                                        pagination?.total
                                    )
                                }

                                {" "}businesses

                            </div>


                            <div className="platform-business-pagination-buttons">

                                <button
                                    type="button"
                                    disabled={
                                        listLoading ||
                                        currentPage <=
                                            1
                                    }
                                    onClick={() =>
                                        handlePageChange(
                                            currentPage -
                                            1
                                        )
                                    }
                                >

                                    Previous

                                </button>


                                <span>

                                    {
                                        currentPage
                                    }

                                </span>


                                <button
                                    type="button"
                                    disabled={
                                        listLoading ||
                                        currentPage >=
                                            totalPages
                                    }
                                    onClick={() =>
                                        handlePageChange(
                                            currentPage +
                                            1
                                        )
                                    }
                                >

                                    Next

                                </button>

                            </div>

                        </div>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | CREATE BUSINESS MODAL
                    |--------------------------------------------------------------------------
                    */}

                    <BusinessModal
                        open={
                            showCreateModal
                        }

                        title="Create New Business"

                        description="Create a tenant, default roles and its first OWNER account."

                        onClose={
                            handleCloseCreateModal
                        }

                        disableClose={
                            createLoading
                        }
                    >

                        <div className="platform-business-modal-body">

                            <PlatformBusinessForm
                                mode="create"

                                loading={
                                    createLoading
                                }

                                onSubmit={
                                    handleCreateBusiness
                                }

                                onCancel={
                                    handleCloseCreateModal
                                }
                            />

                        </div>

                    </BusinessModal>

                </div>


        );

    };


export default PlatformBusinessList;